/**
 * Tool Registry
 * 
 * Singleton registry for all available tools.
 * Tools register themselves at app startup.
 * 
 * @see docs/Research/DesignSpecsClaude.md Section 3.3.1
 */

import type { ITool } from '@/interfaces/ITool';

/**
 * Singleton registry for tool management.
 */
class ToolRegistryImpl {
  private tools: Map<string, ITool> = new Map();
  private categories: Map<string, ITool[]> = new Map();

  /**
   * Register a tool with the registry.
   */
  register(tool: ITool): void {
    if (this.tools.has(tool.id)) {
      console.warn(`Tool "${tool.id}" is already registered. Overwriting.`);
    }
    
    this.tools.set(tool.id, tool);
    
    // Add to category index (deduplicate on re-registration)
    const category = tool.category || 'Other';
    if (!this.categories.has(category)) {
      this.categories.set(category, []);
    }
    const categoryTools = this.categories.get(category)!;
    const existingIndex = categoryTools.findIndex(t => t.id === tool.id);
    if (existingIndex !== -1) {
      categoryTools[existingIndex] = tool;
    } else {
      categoryTools.push(tool);
    }
  }

  /**
   * Get a tool by ID.
   */
  get(toolId: string): ITool | undefined {
    return this.tools.get(toolId);
  }

  /**
   * Check if a tool exists.
   */
  has(toolId: string): boolean {
    return this.tools.has(toolId);
  }

  /**
   * Get all registered tools.
   */
  getAll(): ITool[] {
    return Array.from(this.tools.values());
  }

  /**
   * Get tools by category.
   */
  getByCategory(category: string): ITool[] {
    return this.categories.get(category) || [];
  }

  /**
   * Get all categories with their tools.
   */
  getAllCategories(): Map<string, ITool[]> {
    return new Map(this.categories);
  }

  /**
   * Search tools by query (matches name, description, keywords).
   */
  search(query: string): ITool[] {
    const lowerQuery = query.toLowerCase();
    return this.getAll().filter(tool => {
      const searchText = [
        tool.name,
        tool.description,
        tool.category,
        ...(tool.keywords || [])
      ].join(' ').toLowerCase();
      return searchText.includes(lowerQuery);
    });
  }

  /**
   * Clear all registered tools (mainly for testing).
   */
  clear(): void {
    this.tools.clear();
    this.categories.clear();
  }

  /**
   * Get count of registered tools.
   */
  get count(): number {
    return this.tools.size;
  }
}

// Export singleton instance
export const ToolRegistry = new ToolRegistryImpl();

